import React, { useEffect, useMemo, useState } from 'react';
import { getReports, getReportDomains, deleteReport } from '../utils/api';
import type { ReportSortField, ReportSortOrder } from '../utils/api';
import { SearchIcon } from '../components/Icons';
import ShareModal from '../components/ShareModal';
import ReportsTable from '../components/reports/ReportsTable';
import ReportCards from '../components/reports/ReportCards';
import Pagination from '../components/reports/Pagination';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useApiResource } from '../hooks/useApiResource';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import ApiErrorNotice from '../components/ApiErrorNotice';
import { T } from '../designTokens';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

/** Direction a column reads best in on first click: worst scores and A-Z first. */
const DEFAULT_ORDER: Record<ReportSortField, ReportSortOrder> = {
  date: 'desc',
  score: 'asc',
  domain: 'asc',
};

interface ReportsPageProps {
  /** Scans running or queued for this account; above zero, the page says so and links to the queue. */
  inProgressCount?: number;
  onBack: () => void;
  onViewScan: (scanOrReportId: string) => void;
  onViewPolicyAnalyzerAdmin?: () => void;
  /** Clerk getToken function — forwarded from the parent app. */
  getToken?: () => Promise<string | null>;
  isAdmin?: boolean;
  filterUserId?: string;
}

export default function ReportsPage({ inProgressCount = 0, onBack, onViewScan, onViewPolicyAnalyzerAdmin, getToken, isAdmin, filterUserId }: ReportsPageProps) {
  const isMobile = useIsMobile();
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState<ReportSortField | undefined>(undefined);
  const [order, setOrder] = useState<ReportSortOrder>('desc');
  const [latestPerDomain, setLatestPerDomain] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [actionError, setActionError] = useState<Error | null>(null);
  const [shareReportId, setShareReportId] = useState<string | null>(null);
  const [shareReportName, setShareReportName] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const search = useDebouncedValue(searchInput.trim(), 300);

  const { data: reportsData, loading: isLoading, error, refetch: fetchReports } = useApiResource(
    () => getReports({
      domain: selectedDomain || undefined,
      search: search || undefined,
      sort,
      order: sort ? order : undefined,
      latestPerDomain: latestPerDomain || undefined,
      limit: pageSize,
      offset: (page - 1) * pageSize,
      userId: filterUserId,
    }, getToken),
    [selectedDomain, search, sort, order, latestPerDomain, page, pageSize, filterUserId, getToken]
  );
  const reports = useMemo(() => reportsData?.reports ?? [], [reportsData]);
  const total = reportsData?.total ?? 0;

  const { data: domainData, refetch: fetchDomains } = useApiResource(
    () => getReportDomains(getToken),
    [getToken]
  );
  const domains = domainData?.domains ?? [];

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Selection is page-scoped: drop ids that are no longer on screen so a bulk
  // action can never reach a row the user cannot see.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(reports.map((r) => r.id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [reports]);

  /** Any filter or ordering change invalidates the current page number. */
  const resetToFirstPage = () => setPage(1);

  const handleDomainChange = (domain: string) => {
    setSelectedDomain(domain);
    resetToFirstPage();
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    resetToFirstPage();
  };

  const handleSort = (field: ReportSortField) => {
    if (sort === field) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setOrder(DEFAULT_ORDER[field]);
    }
    resetToFirstPage();
  };

  const handleLatestPerDomainChange = (next: boolean) => {
    setLatestPerDomain(next);
    resetToFirstPage();
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    resetToFirstPage();
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => (prev.size === reports.length ? new Set() : new Set(reports.map((r) => r.id))));
  };

  /** Reload after a delete, stepping back a page if we just emptied this one. */
  const refreshAfterDelete = (deletedCount: number) => {
    if (reports.length - deletedCount <= 0 && page > 1) {
      setPage((p) => p - 1);
    } else {
      fetchReports();
    }
    fetchDomains();
  };

  const handleDeleteReport = async (id: string) => {
    if (!window.confirm('Delete this report? It stays restorable for 30 days.')) return;
    try {
      await deleteReport(id, getToken);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      refreshAfterDelete(1);
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  const handleDeleteSelected = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const noun = ids.length === 1 ? 'report' : 'reports';
    if (!window.confirm(`Delete ${ids.length} ${noun}? They stay restorable for 30 days.`)) return;
    try {
      for (const id of ids) {
        await deleteReport(id, getToken);
      }
      setSelectedIds(new Set());
      refreshAfterDelete(ids.length);
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  const handleShareReport = (id: string, name: string) => {
    setShareReportId(id);
    setShareReportName(name);
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  // The subtitle used to pair a filtered report count with an unfiltered domain
  // count, so filtering to one domain read "3 reports across 1,119 domains".
  const subtitle = selectedDomain
    ? `${total} ${total === 1 ? 'report' : 'reports'} for ${selectedDomain}`
    : search
      ? `${total} ${total === 1 ? 'report' : 'reports'} matching “${search}”`
      : `${total} ${total === 1 ? 'report' : 'reports'} across ${domains.length} ${domains.length === 1 ? 'domain' : 'domains'}`;

  const showInitiatedBy = Boolean(isAdmin && !filterUserId);

  return (
    <>
      <div>
        {/* Header — the "Back to Scanner" button that used to sit here duplicated
            the Scanner tab in the global nav directly above it. */}
        <div data-testid="reports-header-card" style={headerCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h2 style={{ margin: '0 0 8px', color: T.gray800, fontSize: '28px', fontWeight: 700, letterSpacing: '-0.01em' }}>
                {isAdmin ? 'All Reports' : 'Saved Reports'}
                {isAdmin && <span style={adminBadgeStyle}>Admin</span>}
              </h2>
              <p style={{ margin: 0, color: T.gray500, fontSize: '14px' }}>{subtitle}</p>
              {inProgressCount > 0 && (
                <p data-testid="reports-in-progress" style={{ margin: '8px 0 0', fontSize: '14px', color: T.gray600 }}>
                  {inProgressCount === 1 ? '1 audit is' : `${inProgressCount} audits are`} still running or queued.{' '}
                  <a href="#/queue" style={{ color: T.cobalt600, fontWeight: 600 }}>View the queue</a>
                </p>
              )}
            </div>

            {isAdmin && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {onViewPolicyAnalyzerAdmin && (
                  <button onClick={onViewPolicyAnalyzerAdmin} style={secondaryButtonStyle}>
                    Policy Analyzer Requests
                  </button>
                )}
                {!filterUserId && (
                  <a href="#/admin/users" style={{ ...secondaryButtonStyle, textDecoration: 'none' }}>
                    Admin Users
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Toolbar */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginTop: '24px' }}>
            <div style={{ position: 'relative', flex: '1 1 260px', minWidth: '200px' }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: T.gray400, display: 'flex' }}>
                <SearchIcon size={16} />
              </span>
              <input
                type="search"
                role="searchbox"
                aria-label="Search reports by domain or URL"
                placeholder="Search domains and URLs…"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px 10px 36px',
                  fontSize: '14px',
                  border: `1px solid ${T.gray300}`,
                  borderRadius: `${T.radiusMd}px`,
                  backgroundColor: T.white,
                  color: T.gray700,
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {domains.length > 0 && (
              <select
                aria-label="Filter by domain"
                value={selectedDomain}
                onChange={(e) => handleDomainChange(e.target.value)}
                style={{
                  padding: '10px 14px',
                  fontSize: '14px',
                  border: `1px solid ${T.gray300}`,
                  borderRadius: `${T.radiusMd}px`,
                  backgroundColor: T.white,
                  color: T.gray700,
                  cursor: 'pointer',
                  maxWidth: '240px',
                }}
              >
                <option value="">All domains</option>
                {domains.map((d) => (
                  <option key={d.domain} value={d.domain}>
                    {d.domain} ({d.reportCount})
                  </option>
                ))}
              </select>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: T.gray600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={latestPerDomain}
                onChange={(e) => handleLatestPerDomainChange(e.target.checked)}
                aria-label="Show latest scan per domain only"
                style={{ cursor: 'pointer' }}
              />
              Latest scan per domain
            </label>
          </div>
        </div>

        <ApiErrorNotice error={error ?? actionError} onRetry={fetchReports} />

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '48px', color: T.gray500 }}>
            Loading reports…
          </div>
        )}

        {!isLoading && reports.length === 0 && (
          <div style={{ ...cardStyle, padding: '64px 32px', textAlign: 'center' }}>
            <p style={{ color: T.gray400, fontSize: '16px', margin: '0 0 8px' }}>
              {search || selectedDomain ? 'No reports match this filter' : 'No reports saved yet'}
            </p>
            <p style={{ color: T.gray300, fontSize: '14px', margin: '0 0 24px' }}>
              {search || selectedDomain
                ? 'Try a different domain or search term.'
                : 'Run a scan to automatically create a report named by domain.'}
            </p>
            <button onClick={onBack} className="legacy-cta__button">
              Run a scan
            </button>
          </div>
        )}

        {!isLoading && reports.length > 0 && (
          <div style={{ ...cardStyle, padding: '28px' }}>
            {selectedIds.size > 0 && (
              <div style={bulkBarStyle}>
                <span style={{ fontSize: '14px', color: T.gray700, fontWeight: 500 }}>
                  {selectedIds.size} selected
                </span>
                <button onClick={handleDeleteSelected} style={bulkDeleteStyle}>
                  Delete {selectedIds.size} selected
                </button>
                <button onClick={() => setSelectedIds(new Set())} style={bulkClearStyle}>
                  Clear
                </button>
              </div>
            )}

            {isMobile ? (
              <ReportCards
                reports={reports}
                onView={onViewScan}
                onShare={handleShareReport}
                onDelete={handleDeleteReport}
              />
            ) : (
              <ReportsTable
                reports={reports}
                isAdmin={isAdmin}
                showInitiatedBy={showInitiatedBy}
                sort={sort}
                order={order}
                onSort={handleSort}
                selectedIds={selectedIds}
                onToggleOne={toggleOne}
                onToggleAll={toggleAll}
                onView={onViewScan}
                onShare={handleShareReport}
                onDelete={handleDeleteReport}
              />
            )}

            <Pagination
              page={page}
              totalPages={totalPages}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              total={total}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}
      </div>

      {shareReportId && (
        <ShareModal
          reportId={shareReportId}
          reportName={shareReportName}
          onClose={() => { setShareReportId(null); setShareReportName(''); }}
        />
      )}
    </>
  );
}

const cardStyle: React.CSSProperties = {
  backgroundColor: T.white,
  borderRadius: `${T.radiusXl}px`,
  boxShadow: T.shadowMd,
};

const headerCardStyle: React.CSSProperties = {
  ...cardStyle,
  padding: '28px 32px',
  marginBottom: '24px',
};

const adminBadgeStyle: React.CSSProperties = {
  marginLeft: '10px',
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: T.cobalt600,
  background: T.cobalt50,
  border: `1px solid ${T.cobalt200}`,
  borderRadius: `${T.radiusSm}px`,
  padding: '2px 7px',
  verticalAlign: 'middle',
  fontFamily: T.fontBody,
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: '9px 14px',
  fontSize: '13px',
  fontWeight: 600,
  color: T.gray600,
  backgroundColor: T.white,
  border: `1px solid ${T.gray300}`,
  borderRadius: `${T.radiusMd}px`,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
};

const bulkBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '10px 14px',
  marginBottom: '16px',
  backgroundColor: T.cobalt50,
  border: `1px solid ${T.cobalt200}`,
  borderRadius: `${T.radiusMd}px`,
};

const bulkDeleteStyle: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: '13px',
  fontWeight: 600,
  color: T.error,
  backgroundColor: T.white,
  border: `1px solid ${T.errorBorder}`,
  borderRadius: `${T.radiusMd}px`,
  cursor: 'pointer',
};

const bulkClearStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: '13px',
  color: T.gray500,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
};
